import React, { useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Send, Upload, MessageSquare } from "lucide-react";

export default function WhatsApp() {
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [newWa, setNewWa] = useState("");
  const [liveMode, setLiveMode] = useState(false);
  const fileRef = useRef();
  const scrollRef = useRef();

  const loadConvs = async () => {
    const r = await api.get("/whatsapp/conversations");
    setConvs(r.data);
  };
  useEffect(() => { loadConvs(); }, []);

  const openConv = async (c) => {
    setActive(c);
    const r = await api.get(`/whatsapp/messages/${c.wa_number}`);
    setMsgs(r.data);
    setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
  };

  const send = async () => {
    if (!input.trim() || !active) return;
    const text = input; setInput("");
    const r = await api.post("/whatsapp/simulate", { from_number: active.wa_number, text, live_mode: liveMode });
    setMsgs(r.data.messages);
    loadConvs();
    setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
  };

  const startNew = async () => {
    if (!newWa.trim()) return;
    await api.post("/whatsapp/simulate", { from_number: newWa.trim(), text: "Hi", live_mode: liveMode });
    setNewWa("");
    await loadConvs();
    const r = await api.get(`/whatsapp/messages/${newWa.trim()}`);
    setActive({ wa_number: newWa.trim(), customer_name: "" });
    setMsgs(r.data);
  };

  const uploadBill = async (file) => {
    if (!file || !active) return;
    const fd = new FormData();
    fd.append("from_number", active.wa_number);
    fd.append("file", file);
    fd.append("live_mode", liveMode ? "true" : "false");
    const r = await api.post("/whatsapp/simulate-bill", fd);
    setMsgs(r.data.messages);
    loadConvs();
    setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
  };

  return (
    <Layout title="WhatsApp" subtitle="Conversation simulator"
      action={
        <label className="flex items-center gap-3 cursor-pointer" data-testid="live-mode-toggle">
          <span className="label-tiny">Live mode (real Karix sends)</span>
          <span className={`relative inline-block w-10 h-5 rounded-full transition ${liveMode ? "bg-[#4A6741]" : "bg-[#E6E4E0]"}`}>
            <span className={`absolute top-0.5 ${liveMode ? "left-5" : "left-0.5"} w-4 h-4 bg-white rounded-full transition-all shadow`} />
          </span>
          <input type="checkbox" className="hidden" checked={liveMode} onChange={(e) => setLiveMode(e.target.checked)} />
        </label>
      }>
      {liveMode && (
        <div className="mb-4 text-[12px] text-[#8A6F1F] bg-[#FBF4E0] p-3 border border-[#F0E4B5]">
          ⚡ Live mode is ON. Bot replies will be sent to the recipient via Karix. WhatsApp 24-hour session rules apply — sends will fail with statusCode 500 outside the window.
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5 h-[calc(100vh-220px)]">
        {/* Conversations list */}
        <div className="card-sharp flex flex-col overflow-hidden">
          <div className="p-4 border-b border-[#E6E4E0]">
            <div className="label-tiny mb-2">Simulate new chat</div>
            <div className="flex gap-2">
              <input className="fin flex-1" placeholder="+91XXXXXXXXXX" value={newWa} onChange={(e) => setNewWa(e.target.value)} data-testid="new-wa-number" />
              <button className="btn-dark" onClick={startNew} data-testid="start-wa">Hi</button>
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {convs.map((c) => (
              <div key={c.wa_number}
                   onClick={() => openConv(c)}
                   className={`p-4 border-b border-[#E6E4E0] cursor-pointer hover:bg-[#FAFAF8] ${active?.wa_number===c.wa_number?"bg-[#FAFAF8]":""}`}
                   data-testid={`conv-${c.wa_number}`}>
                <div className="flex items-center justify-between">
                  <div className="font-medium text-sm">{c.customer_name}</div>
                  <div className="text-[10px] text-[#8C8C8C]">{new Date(c.last_at).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}</div>
                </div>
                <div className="text-[12px] text-[#8C8C8C] truncate mt-1">{c.last_message}</div>
                <div className="font-mono text-[10px] text-[#8C8C8C] mt-1">{c.wa_number}</div>
              </div>
            ))}
            {convs.length === 0 && <div className="text-center text-[#8C8C8C] text-sm p-8">No conversations yet. Start one above.</div>}
          </div>
        </div>

        {/* Chat */}
        <div className="card-sharp flex flex-col overflow-hidden">
          {active ? (
            <>
              <div className="p-4 border-b border-[#E6E4E0] flex items-center justify-between">
                <div>
                  <div className="font-medium">{active.customer_name || active.wa_number}</div>
                  <div className="text-[12px] text-[#8C8C8C] font-mono">{active.wa_number}</div>
                </div>
                <div className="text-[11px] text-[#8C8C8C]">via Karix WABA • Powered by Fundle</div>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-[#F9F8F6]">
                {msgs.map((m) => (
                  <div key={m.id} className={m.direction === "in" ? "" : "flex flex-col items-end"}>
                    <div className={m.direction === "in" ? "wa-in" : "wa-out"}>{m.text}</div>
                    {m.direction === "out" && m.delivery_status && (
                      <div className="text-[10px] text-[#8C8C8C] mt-1 flex items-center gap-1">
                        {m.delivery_status === "sent" && <span className="text-[#4A6741]">✓✓ sent via Karix</span>}
                        {m.delivery_status === "failed" && <span className="text-[#8C3A3A]" title={m.karix_status_desc || m.error}>⚠ failed{m.karix_status_code ? ` (${m.karix_status_code})` : ""}</span>}
                        {m.delivery_status === "simulated" && <span>· simulated (live mode off)</span>}
                        {m.delivery_status === "queued" && <span>· queued</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-[#E6E4E0] flex gap-2 items-center">
                <button className="btn-outline" onClick={() => fileRef.current?.click()} data-testid="upload-bill-sim"><Upload size={14} /></button>
                <input ref={fileRef} type="file" hidden onChange={(e) => uploadBill(e.target.files?.[0])} accept="image/*,application/pdf" />
                <input className="fin flex-1" placeholder="Type a message…" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key==="Enter" && send()} data-testid="wa-input" />
                <button className="btn-dark flex items-center gap-2" onClick={send} data-testid="wa-send"><Send size={14} /></button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-[#8C8C8C]">
              <div className="text-center">
                <MessageSquare size={40} className="mx-auto mb-3 opacity-30" />
                <div>Select a conversation or start a new simulation.</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
